import { useEffect, useRef, useState } from "react";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right.js";
import CheckCircle2 from "lucide-react/dist/esm/icons/check-circle-2.js";
import Clock3 from "lucide-react/dist/esm/icons/clock-3.js";
import MapPin from "lucide-react/dist/esm/icons/map-pin.js";
import Monitor from "lucide-react/dist/esm/icons/monitor.js";
import Navigation from "lucide-react/dist/esm/icons/navigation.js";
import Search from "lucide-react/dist/esm/icons/search.js";
import Share2 from "lucide-react/dist/esm/icons/share-2.js";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check.js";
import "./marketing.css";

const APP_PREVIEW_URL = "/app";
const VENUE_INTAKE_URL = "https://docs.google.com/forms/d/e/1FAIpQLSdsiq_pgyrS7KjB7laxuU1nis5A3hBlkMKsoIppHjC2fXpsyA/viewform";

function ArrowLink({ children, className = "", href = APP_PREVIEW_URL, newTab = false }) {
  return (
    <a
      className={`marketing-arrow-link ${className}`}
      href={href}
      target={newTab ? "_blank" : undefined}
      rel={newTab ? "noreferrer" : undefined}
    >
      <span>{children}</span>
      <ArrowRight aria-hidden="true" size={17} />
    </a>
  );
}

function BrandLockup({ footer = false }) {
  return (
    <img
      className={`brand-wordmark${footer ? " brand-wordmark-footer" : ""}`}
      src="/brand/downloads/fadius-horizontal-lockup-transparent.png?v=20261023"
      alt="Fadius"
    />
  );
}

function Header() {
  return (
    <header className="marketing-header">
      <a className="marketing-logo" href="#top" aria-label="Fadius home"><BrandLockup /></a>
      <nav className="marketing-nav" aria-label="Primary navigation">
        <a href="#story">The problem</a>
        <a href="#how-it-works">How it works</a>
        <a href="#confirmations">Confirmed places</a>
        <a href="#for-venues">Bars &amp; groups</a>
      </nav>
      <ArrowLink className="marketing-header-cta">Open preview</ArrowLink>
      <a className="marketing-header-pill" href={APP_PREVIEW_URL}>Open preview</a>
    </header>
  );
}

function ConfirmedVenueCard({ compact = false }) {
  return (
    <div className={`confirmed-card${compact ? " confirmed-card-compact" : ""}`}>
      <div className="confirmed-card-topline">
        <span className="match-identity"><span className="league-badge" aria-hidden="true">MLS</span><span className="match-code">NYC–NYRB</span></span>
        <span className="live-chip"><span className="live-dot" aria-hidden="true" />Live · 62′</span>
      </div>
      <div className="confirmed-screen-signal"><Monitor size={14} aria-hidden="true" />Multiple screens</div>
      <div className="confirmed-place">
        <span className="confirmed-icon"><CheckCircle2 size={18} /></span>
        <div>
          <strong>Northside Sports Bar</strong>
          <span>Confirmed by the venue today</span>
        </div>
        <span className="confirmed-distance">1.2 mi</span>
      </div>
      <div className="confirmed-actions">
        <span><Navigation size={14} /> Directions</span>
        <span><Share2 size={14} /> Share</span>
        <span className="sample-label">Illustrative</span>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="lean-hero" id="top">
      <Header />
      <div className="hero-grid">
        <div className="hero-copy-block">
          <p className="hero-badge"><span className="live-dot" aria-hidden="true" />Soccer Watch</p>
          <h1>Find a bar that is actually showing the match.</h1>
          <p className="hero-copy">See nearby bars confirmed by the venue or by a fan, so you can stop guessing and calling around.</p>
          <div className="hero-actions">
            <ArrowLink className="button-primary">Find a place to watch</ArrowLink>
            <a className="text-link" href="#how-it-works">How it works</a>
          </div>
          <p className="preview-note">Illustrative preview · Soccer only</p>
        </div>
        <div className="hero-product" aria-label="Illustrative Fadius venue confirmation preview">
          <div className="hero-phone-top">
            <img src="/brand/downloads/fadius-app-icon-transparent.png?v=20261023" alt="" />
            <span>Where is NYC–NYRB on?</span>
          </div>
          <div className="hero-map" aria-hidden="true">
            <span className="map-road map-road-one" /><span className="map-road map-road-two" />
            <span className="map-ring map-ring-one" /><span className="map-ring map-ring-two" /><span className="map-ring map-ring-three" />
            <span className="map-sweep" />
            <span className="map-ping" /><span className="map-ping map-ping-late" />
            <span className="map-you">You</span>
            <span className="map-dot map-dot-one"><MapPin size={18} /></span>
            <span className="map-dot map-dot-two"><MapPin size={18} /></span>
          </div>
          <ConfirmedVenueCard compact />
        </div>
      </div>
    </section>
  );
}

const CHAT_AVATARS = ["A", "J", "M", "S", "C", "T", "R", "K"];
const CHAT_MESSAGES = [
  { from: "Alex", text: "Big NYC–NYRB match Sunday. Where are we watching?" },
  { mine: true, text: "Somewhere that actually has it on lol" },
  { from: "Jordan", text: "Does that place on the north side show it?" },
  { from: "Alex", text: "I think so? Haven’t been there in forever" },
  { mine: true, text: "Their site doesn’t say anything" },
  { from: "Jordan", text: "Cool, so we’re calling bars again? 😂" },
];

function GroupChat() {
  const frameRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [typingIndex, setTypingIndex] = useState(null);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion || typeof IntersectionObserver === "undefined") {
      setHasStarted(true);
      setVisibleCount(CHAT_MESSAGES.length);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHasStarted(true);
        observer.disconnect();
      }
    }, { threshold: 0.35 });
    if (frameRef.current) observer.observe(frameRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!hasStarted || visibleCount >= CHAT_MESSAGES.length) return undefined;
    const nextMessage = CHAT_MESSAGES[visibleCount];
    const showTyping = !nextMessage.mine;
    if (showTyping) setTypingIndex(visibleCount);
    const revealTimer = window.setTimeout(() => {
      setTypingIndex(null);
      setVisibleCount((count) => count + 1);
    }, showTyping ? 720 : 460);
    return () => window.clearTimeout(revealTimer);
  }, [hasStarted, visibleCount]);

  return (
    <div ref={frameRef} className="chat-frame" role="group" aria-label="Fictional Sunday League group chat asking where to watch the NYC–NYRB match">
      <div className="chat-head">
        <div className="chat-avatars" aria-hidden="true">{CHAT_AVATARS.map((letter) => <span key={letter}>{letter}</span>)}</div>
        <strong>Sunday League ⚽</strong>
        <span>8 people</span>
      </div>
      <ol className="chat-thread">
        <li className="chat-day">Sunday 11:02 AM</li>
        {CHAT_MESSAGES.map((message, index) => (
          <li key={message.text} className={`chat-message${message.mine ? " chat-message-mine" : ""}${index < visibleCount ? " chat-message-visible" : ""}`} aria-hidden={index >= visibleCount}>
            {message.from && <span className="chat-sender">{message.from}</span>}
            <p>{message.text}</p>
          </li>
        ))}
        <li className={`chat-typing${typingIndex === null ? "" : " chat-typing-visible"}`} aria-hidden="true">
          <span /><span /><span />
        </li>
      </ol>
      <div className="chat-compose" aria-hidden="true">Message</div>
    </div>
  );
}

function GroupChatStory() {
  return (
    <section className="chat-story section-pad" id="story">
      <div className="chat-copy">
        <h2>Finding the match shouldn’t take a group chat.</h2>
        <p>Your group knows the match. Nobody knows which bar will actually have it on. Fadius gives you a confirmed answer before anyone leaves home.</p>
        <span className="recreation-label">Illustrative conversation based on a common fan experience.</span>
      </div>
      <GroupChat />
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Search, number: "1", title: "Pick the match", body: "Start with the soccer game you want to watch." },
    { icon: ShieldCheck, number: "2", title: "See confirmed places", body: "Know whether the venue or a fan confirmed it, and when." },
    { icon: Navigation, number: "3", title: "Go, or share it", body: "Open directions or send the confirmed place back to the group." },
  ];
  return (
    <section className="how-section section-pad" id="how-it-works">
      <div className="section-heading">
        <h2>Pick the match.<br />Find the place.</h2>
      </div>
      <ol className="step-list">
        {steps.map(({ icon: Icon, number, title, body }) => (
          <li className="step-item" key={number}>
            <span className="step-number" aria-hidden="true">{number}</span>
            <div>
              <h3><Icon aria-hidden="true" size={20} />{title}</h3>
              <p>{body}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function TrustSection() {
  return (
    <section className="trust-section section-pad" id="confirmations">
      <div className="trust-copy">
        <h2>See who confirmed the match, and when.</h2>
        <p>Bars can confirm ahead of time. Fans can confirm from the venue.</p>
      </div>
      <div className="trust-grid">
        <article>
          <span className="source-chip"><ShieldCheck size={16} aria-hidden="true" />Confirmed by the venue</span>
          <h3>Straight from the bar</h3>
          <p>The bar confirms it plans to show the match.</p>
          <strong>Confirmed today</strong>
        </article>
        <article>
          <span className="source-chip"><Clock3 size={16} aria-hidden="true" />Confirmed by a fan</span>
          <h3>From someone who is there</h3>
          <p>A fan at the bar confirms the match is on.</p>
          <strong>Confirmed 12 minutes ago</strong>
        </article>
      </div>
      <p className="trust-note">No confirmation? We label the place as a reference, not a sure thing.</p>
    </section>
  );
}

function VenueSection() {
  return (
    <section className="venue-section section-pad" id="for-venues">
      <div className="venue-copy">
        <h2>Show matches at your bar? Help fans find you.</h2>
        <p>Tell us which teams or competitions you show. We’ll verify each match before listing it.</p>
      </div>
      <div className="venue-action">
        <div className="venue-action-copy">
          <ArrowLink className="button-primary" href={VENUE_INTAKE_URL} newTab>Tell us what you show</ArrowLink>
          <p>Use the short form. Submitting does not create a listing or partnership.</p>
        </div>
        <a className="venue-qr" href={VENUE_INTAKE_URL} target="_blank" rel="noreferrer" aria-label="Open the bars and supporters group form">
          <img src="/marketing/fadius-venue-intake-qr.png" alt="QR code for the Fadius bars and supporters group form" />
          <span>Scan to open the form</span>
        </a>
      </div>
    </section>
  );
}

function CoverageSection() {
  return (
    <section className="coverage-section section-pad">
      <div>
        <h2>Soccer first.<br />Select markets.</h2>
        <ul className="league-list" aria-label="Domestic and international soccer competitions represented in the product direction">
          <li>MLS</li><li>NWSL</li><li>USL</li><li>NCAA soccer</li><li>EPL</li><li>UCL / UEL / UECL</li><li>International soccer</li>
        </ul>
      </div>
      <div className="coverage-copy">
        <p>Building one place to find soccer viewing across domestic and international leagues, starting in select markets.</p>
        <p>More sports and Attend come next.</p>
      </div>
    </section>
  );
}

function ConversionClose() {
  return (
    <section className="conversion-close" id="close">
      <img src="/brand/downloads/fadius-app-icon-transparent.png?v=20261023" alt="" className="conversion-mark" />
      <h2>Find the match. Pick the place. Go.</h2>
      <p>See nearby bars confirmed to be showing the game.</p>
      <ArrowLink className="button-primary button-primary-light">Open the preview</ArrowLink>
    </section>
  );
}

function Footer() {
  return (
    <footer className="marketing-footer" id="footer">
      <div className="footer-brand"><BrandLockup footer /><p>Find where the match is on.</p></div>
      <div className="footer-links">
        <a href="#story">The problem</a><a href="#how-it-works">How it works</a><a href="#confirmations">Confirmed places</a><a href="#for-venues">Bars &amp; groups</a><a href={APP_PREVIEW_URL}>Watch preview</a><a href="/privacy">Privacy scope</a><a href="/terms">Terms scope</a>
      </div>
      <div className="footer-bottom"><span>© 2026 Fadius. Early product preview.</span><span>Fadius™ · Early beta</span></div>
    </footer>
  );
}

// Phone-only call to action: appears once the hero has scrolled away and hides again near the end.
function StickyCta() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const watched = { top: true, close: false, footer: false };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { watched[entry.target.dataset.watch] = entry.isIntersecting; });
      setShow(!watched.top && !watched.close && !watched.footer);
    }, { threshold: 0.01 });
    [["top", "top"], ["close", "close"], ["footer", "footer"]].forEach(([key, id]) => {
      const node = document.getElementById(id);
      if (node) { node.dataset.watch = key; observer.observe(node); }
    });
    return () => observer.disconnect();
  }, []);
  return (
    <div className={`sticky-cta${show ? " sticky-cta-visible" : ""}`}>
      <ArrowLink className="button-primary">Find a place to watch</ArrowLink>
    </div>
  );
}

export function MarketingSite() {
  return <main className="marketing-site"><Hero /><GroupChatStory /><HowItWorks /><TrustSection /><VenueSection /><CoverageSection /><ConversionClose /><Footer /><StickyCta /></main>;
}
